import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import CylindricalShellTab from "./CylindricalShellTab";
import { useFormContext } from "react-hook-form";
import EllipticalHeadTab from "./EllipticalHeadTab";
import Button from "@mui/material/Button";
import { calculate } from "../API`s/pv/sqliteApi";
import axios from "../../../../../api/axios";
import checkRequiredFields from "../../../../../utils/checkRequiredFields";
import CustomAlert from "../../../../../components/widgets/CustomAlert";
import NozzleTab from "./NozzleTab";
import SaddleAnalysisTab from "./SaddleAnalysisTab";
import { Stack } from "@mui/material";
import { verticalTabStyle } from "../../../../../styles/muiProps";
import CalcTabTemplate from "./CalcTabTemplate";
import {
   Anchor_Bolt_InputData,
  Anchor_Bolt_OutputData,
  Base_Ring_InputData,
  Base_Ring_OutputData,
  Deflection_InputData,
  Deflection_OutputData,
  Leg_Support_InputData,
  Leg_Support_OutputData,
  Lug_Support_InputData,
  Lug_Support_OutputData,
  Seismic_Load_InputData,
  Seismic_Load_OutputData,
  Skirt_Support_InputData,
  Skirt_Support_OutputData,
  Stress_Moment_InputData,
  Stress_Moment_OutputData,
  Wind_Load_InputData,
  Wind_Load_OutputData,
} from "./inputFields/otherTabsInput";

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
      width="100%"
      style={{ width: "90%" }}
    >
      {value === index && (
        <Box sx={{ p: 3, minHeight: "100vh" }} width="inherit">
          <div
            id="tab-div"
            style={{ width: "100%", display: "flex", flexDirection: "column" }}
          >
            {children}
          </div>
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `vertical-tab-${index}`,
    "aria-controls": `vertical-tabpanel-${index}`,
  };
}

const SubmitButtonStyle = {
  width: "fit-content",
  alignSelf: "flex-end",
};

const CalculationTab = (props) => {
  document.title = "Pressure Vessel - Calculation";

  const [tab, setTab] = React.useState(0);
  const [calculationResult, setCalculationResult] = React.useState([]);
  const { setValue, getValues } = useFormContext();
  const [openAlert, setOpenAlert] = useState(false);
  const [AlertContent, setAlertContent] = useState({
    severity: "",
    message: "",
  });

  const defaults = {
    "Liquid Head Pressure": 0.1,
    "Material Specification": "SA 516",
    "Allowable Stress": 138,
    "Provided Thk": 12,
    "Corrosion Allowance": 3,
  };

  const autoPopulateFields = () => {
    const allValues = getValues();
    const shellRows = allValues?.DataGridView5 || [];
    const headRow = shellRows.find(r => {
      const c = (r?.Component || "").toLowerCase();
      return c.includes("head") || c.includes("elliptical") || c.includes("torispherical") || c.includes("spherical") || c.includes("flat");
    });
    const shellRow = shellRows.find(r => (r?.Component || "").toLowerCase().includes("shell"));
    const dia = shellRow?.Diameter || allValues.txtVIDia || "";
    const thk = shellRow?.["Provided Thk"] || "";
    const canLen = shellRow?.["Can Length"] || "";
    const headDia = headRow?.Diameter || allValues.txtVIDia || "";
    const headThk = headRow?.["Provided Thk"] || "";

    const diaNum = parseFloat(dia) || 0;
    const thkNum = parseFloat(thk) || 0;
    const canLenNum = parseFloat(canLen) || 0;

    const setIf = (field, val) => { if (val) setValue(field, val); };

    // Vessel weight estimation for default calculations
    const steelDensity = 7.85e-6;
    const headThkNum = parseFloat(headThk || thk) || 0;
    const estShellMass = Math.PI * (diaNum + thkNum) * canLenNum * thkNum * steelDensity;
    const estHeadMass = 1.084 * diaNum * diaNum * headThkNum * steelDensity;
    const estWeightKg = estShellMass + 2 * estHeadMass;
    const estWeightN = Math.round(estWeightKg * 9.81);
    const estWeightLbs = Math.round(estWeightKg * 2.20462);

    // Estimate base moment from wind pressure
    const windP = parseFloat(allValues.PW_Pressure_WL) || 0;
    let estBaseMoment = 0;
    if (windP && diaNum && canLenNum) {
      const V = windP * diaNum * canLenNum;
      estBaseMoment = Math.round(V * canLenNum / 2);
    }

    // --- Cylindrical Shell ---
    setIf("txt_DP_CSC", allValues.txtDPressure);
    setIf("txt_DT_CSC", allValues.txtDTemp);
    setIf("txt_AS_CSC", allValues.TextBox68);
    setIf("txt_JE_CSC", allValues.TextBox31);
    setIf("txt_ID_CSC", dia);
    setIf("txt_CA_CSC", allValues.txtVCA);
    setIf("txt_DL_CSC", canLen);
    setIf("TextBox6", allValues.TextBox92);
    setIf("txt_MS_CSC", allValues.ComboBox9);
    setIf("txt_PT_CSC", thk);

    // --- Elliptical Head ---
    setIf("txt_DP_EHC", allValues.txtDPressure);
    setIf("txt_DT_EHC", allValues.txtDTemp);
    setIf("txt_AS_EHC", allValues.TextBox68);
    setIf("txt_JE_EHC", allValues.TextBox31);
    setIf("txt_ID_EHC", headDia);
    setIf("txt_CA_EHC", allValues.txtVCA);
    setIf("txt_MS_EHC", allValues.ComboBox9);
    setIf("TextBox13", allValues.TextBox92);
    setIf("txt_PT_EHC", headThk);

    // --- Other head types ---
    setIf("txt_DP_SHC", allValues.txtDPressure);
    setIf("txt_DP_THC", allValues.txtDPressure);
    setIf("txt_DP_FHC", allValues.txtDPressure);
    setIf("txt_DP_COSC", allValues.txtDPressure);
    setIf("txt_DT_SHC", allValues.txtDTemp);
    setIf("txt_DT_THC", allValues.txtDTemp);
    setIf("txt_DT_COSC", allValues.txtDTemp);
    setIf("txt_AS_SHC", allValues.TextBox68);
    setIf("txt_AS_THC", allValues.TextBox68);
    setIf("txt_AS_FHC", allValues.TextBox68);
    setIf("txt_AS_COSC", allValues.TextBox68);
    if (allValues.TextBox31) {
      setValue("txt_JE_SHC", allValues.TextBox31);
      setValue("txt_JE_THC", allValues.TextBox31);
      setValue("txt_JE_FHC", allValues.TextBox31);
      setValue("txt_JE_COSC", allValues.TextBox31);
    }
    setIf("txt_ID_SHC", headDia);
    setIf("txt_ID_THC", headDia);
    setIf("txt_ID_FHC", headDia);
    setIf("txt_DLE_COSC", headDia);
    setIf("txt_CA_SHC", allValues.txtVCA);
    setIf("txt_CA_THC", allValues.txtVCA);
    setIf("txt_CA_FHC", allValues.txtVCA);
    setIf("txt_CA_COSC", allValues.txtVCA);
    setIf("txt_PT_SHC", headThk);
    setIf("txt_PT_THC", headThk);
    setIf("txt_PT_FHC", headThk);
    setIf("txt_PT_COSC", headThk);

    // --- Wind Load ---
    setIf("D_Diameter_WL", dia);
    setIf("D1_Diameter_WL", allValues.txtVIDia ? parseFloat(allValues.txtVIDia) + 2 * parseFloat(thk || 0) : "");
    setIf("E_Joint_Efficiency_WL", allValues.TextBox31);
    setIf("H_Height_WL", canLen);
    setIf("P_Pressure_WL", allValues.txtDPressure);
    setIf("S_Allowable_Stress_WL", allValues.TextBox68);

    // Derive wind pressure from wind speed (TextBox49 in km/hr)
    // PW = 0.613 * (v/3.6)^2 Pa = v^2 * 4.73e-8 N/mm^2
    if (allValues.TextBox49 && !allValues["PW_Pressure_WL"]) {
      const windSpeed = parseFloat(allValues.TextBox49);
      if (windSpeed) {
        const pw = windSpeed * windSpeed * 4.73e-8;
        setValue("PW_Pressure_WL", parseFloat(pw.toFixed(8)));
      }
    }

    // --- Seismic Load ---
    setIf("D_Diameter_SL", dia);
    setIf("H_Length_SL", canLen);
    if (!allValues["X_Distance_SL"]) setValue("X_Distance_SL", canLenNum ? canLenNum / 2 : "");
    if (!allValues["W_Weight_SL"]) setValue("W_Weight_SL", estWeightN);

    // --- Deflection ---
    setIf("D1_Diameter_Df", dia);
    setIf("H_Length_Df", canLen);
    setIf("PW_Pressure_Df", allValues.txtDPressure);
    setIf("R_Mean_Radius_Df", dia ? parseFloat(dia) / 2 : "");
    setIf("T_Thickness_Df", thk);

    // --- Skirt Support ---
    setIf("D_Diameter_SS", dia);
    setIf("S_Allowable_Stress_SS", allValues.TextBox68);
    setIf("E_Joint_Efficiency_SS", allValues.TextBox31);
    if (!allValues["Mt_Moment_Bottom_SS"] && estBaseMoment) setValue("Mt_Moment_Bottom_SS", Math.round(estBaseMoment * 12));
    if (!allValues["W_Weight_SS"]) setValue("W_Weight_SS", estWeightN);

    // --- Anchor Bolt ---
    setIf("S_Allowable_Stress_AB", allValues.TextBox68);
    if (!allValues["R_Radius_AB"]) setValue("R_Radius_AB", diaNum ? Math.round(diaNum / 2 + 100) : "");
    if (!allValues["Ab_Area_of_Bolt_AB"]) setValue("Ab_Area_of_Bolt_AB", Math.round(Math.PI * 24 * 24 / 4));
    if (!allValues["Cb_Circumference_AB"]) {
      const r = diaNum ? Math.round(diaNum / 2 + 100) : 0;
      if (r) setValue("Cb_Circumference_AB", Math.round(2 * Math.PI * r));
    }
    if (!allValues["M_Moment_AB"] && estBaseMoment) setValue("M_Moment_AB", Math.round(estBaseMoment * 12));
    if (!allValues["W_Weight_AB"]) setValue("W_Weight_AB", estWeightN);

    // --- Base Ring ---
    setIf("D_Dia_of_Skirt_BR", dia);
    if (!allValues["M_Moment_BR"] && estBaseMoment) setValue("M_Moment_BR", Math.round(estBaseMoment * 12));
    if (!allValues["R_Radius_BR"]) setValue("R_Radius_BR", diaNum ? Math.round(diaNum / 2 + 50) : "");
    if (!allValues["Ab_Area_of_Bolt_BR"]) setValue("Ab_Area_of_Bolt_BR", Math.round(Math.PI * 24 * 24 / 4));
    if (!allValues["Cb_Circumference_BR"]) {
      const r = diaNum ? Math.round(diaNum / 2 + 50) : 0;
      if (r) setValue("Cb_Circumference_BR", Math.round(2 * Math.PI * r));
    }
    if (!allValues["W_Weight_BR"]) setValue("W_Weight_BR", estWeightN);

    // --- Lug Support ---
    setIf("R_Radius_of_Head_Lug", dia ? parseFloat(dia) / 2 : "");
    setIf("S_Stress_Lug", allValues.TextBox68);
    setIf("P_Design_Pressure_Lug", allValues.txtDPressure);
    setIf("E_Joint_Efficiency_Lug", allValues.TextBox31);
    setIf("t_wall_thickness_Lug", thk);
    if (!allValues["W_Weight_of_vessel_pounds_Lug"]) setValue("W_Weight_of_vessel_pounds_Lug", estWeightLbs);
    if (!allValues["Q_Load_on_One_Leg_Lug"]) {
      const n = parseFloat(allValues.n_number_of_legs_Lug) || 4;
      setValue("Q_Load_on_One_Leg_Lug", Math.round(estWeightLbs / n));
    }

    // --- Leg Support ---
    setIf("R_Radius_of_Head_Leg", dia ? parseFloat(dia) / 2 : "");
    setIf("S_Stress_Leg", allValues.TextBox68);
    setIf("P_Design_Pressure_Leg", allValues.txtDPressure);
    setIf("E_Joint_Efficiency_Leg", allValues.TextBox31);
    setIf("t_wall_thickness_Leg", thk);
    if (!allValues["W_Weight_of_vessel_pounds_Leg"]) setValue("W_Weight_of_vessel_pounds_Leg", estWeightLbs);
    if (!allValues["Q_Load_on_One_Leg_Leg"]) {
      const n = parseFloat(allValues.n_number_of_legs_Leg) || 4;
      setValue("Q_Load_on_One_Leg_Leg", Math.round(estWeightLbs / n));
    }

    // --- Saddle Zick Analysis ---
    setIf("txt_R_SC", dia ? parseFloat(dia) / 2 : "");
    setIf("txt_AS_SC", allValues.TextBox68);
    setIf("txt_LS_SC", canLen);
    setIf("txt_TS_SC", thk);
    setIf("txt_LH_SC", headRow?.["Can Length"] || "");
    setIf("txt_TH_SC", headRow?.["Provided Thk"] || "");
    setIf("txt_SD_SC", canLen ? parseFloat(canLen) / 4 : "");
    if (allValues.txt_CA_SC == null) setValue("txt_CA_SC", 120);
    if (allValues.txt_Q_SC == null) {
      setValue("txt_Q_SC", Math.round(estWeightKg / 2));
    }

    // Liquid head defaults
    setIf("txt_LH_CSC", defaults["Liquid Head Pressure"]);
    setIf("txt_LH_EHC", defaults["Liquid Head Pressure"]);

    // Apply defaultValue for isUserField fields that have one
    const allInputData = [
      Wind_Load_InputData, Seismic_Load_InputData,
      Deflection_InputData, Skirt_Support_InputData,
      Anchor_Bolt_InputData, Base_Ring_InputData,
      Lug_Support_InputData, Leg_Support_InputData,
      Stress_Moment_InputData,
    ];
    for (const arr of allInputData) {
      for (const field of arr) {
        if (field.isUserField && field.defaultValue != null && (allValues[field.txtFieldName] === undefined || allValues[field.txtFieldName] === "")) {
          setValue(field.txtFieldName, field.defaultValue);
        }
      }
    }
  };

  const getCalc = async (data) => {
    const allValues = getValues();
    const shellRows = allValues?.DataGridView5 || [];
    const nozzleRows = allValues?.DataGridView1 || [];

    autoPopulateFields();

    const shellRow = shellRows.find(r => r.Component?.toLowerCase().includes("shell"));

    // Auto-populate Nozzle fields from Design tab, Shell tab & selected nozzle
    const selectedIdx = allValues.Cmb_Nz_Mark;
    const selectedNozzle = nozzleRows[selectedIdx];
    if (selectedNozzle) {
      if (shellRow?.Diameter) setValue("txt_SD_NC1", shellRow.Diameter);
      if (allValues.txtDPressure) setValue("txt_DP_NC1", allValues.txtDPressure);
      if (allValues.ComboBox9) setValue("txt_SM_NC1", allValues.ComboBox9);
      if (shellRow?.["Provided Thk"]) setValue("txt_SPT_NC1", shellRow["Provided Thk"]);
      if (allValues.TextBox68) setValue("txt_SAS_NC1", allValues.TextBox68);
      if (allValues.ComboBox12) setValue("txt_NM_NC1", allValues.ComboBox12);
      if (allValues.TextBox78) setValue("txt_NAS_NC1", allValues.TextBox78);
      if (selectedNozzle["Nozzle Weld (A)"]) setValue("txt_FWSI_NC1", selectedNozzle["Nozzle Weld (A)"]);
      if (selectedNozzle["Inside Proj"]) setValue("txt_FWSO_NC1", selectedNozzle["Inside Proj"]);

      try {
        const allTables = await window.electron.db.getAllTables("pv");
        const pipeKey = "#Tube & Pipe#Conduits#Pipes#ASME B16 Pipe";
        const pipeTable = Array.isArray(allTables[pipeKey]) ? allTables[pipeKey] : [];
        const ndRaw = String(selectedNozzle.Size || "").replace(/\s+/g, "").trim();
        const snRaw = String(selectedNozzle["Sch/Thk"] || "").replace(/\s+/g, "").trim();
        const matchedPipe = pipeTable.find(
          row => String(row.ND).trim() === ndRaw && String(row.SN).toString().trim() === snRaw
        );
        if (matchedPipe) {
          const odVal = matchedPipe.OD || matchedPipe.OuterDia || matchedPipe["Outer Diameter"];
          const tVal = matchedPipe.t || matchedPipe.Thk || matchedPipe.Thickness || matchedPipe["Wall Thk"];
          if (odVal) setValue("txt_ND_NC1", parseFloat(odVal));
          if (tVal) setValue("txt_NPT_NC1", parseFloat(tVal));
        }
      } catch (_err) {
      }
    }

    const updatedData = getValues();

    const result = checkRequiredFields(updatedData);
    if (result[0] === false) {
      setAlertContent({
        message: "Following values are missing: " + result[1],
        severity: "error",
      });
      setOpenAlert(true);
    } else {
      const res = await calculate(updatedData);
      setCalculationResult(res.data);
      for (let elem of ["Cylindrical", "Elliptical", "Nozzle", "Saddle", "Torispherical", "Spherical", "Flat", "Conical", "Wind", "Seismic", "Deflection", "StressMoment", "Leg", "Lug", "Skirt", "AnchorBolt", "BaseRing"]) {
        if (res.data[elem]) {
          for (const [key, value] of Object.entries(res.data[elem])) {
            setValue(key, value);
          }
        }
      }
    }
  };

  useEffect(() => {
    autoPopulateFields();
    for (let elem of ["Cylindrical", "Elliptical", "Nozzle", "Saddle"]) {
      if (calculationResult[elem]) {
        for (const [key, value] of Object.entries(calculationResult[elem])) {
          setValue(key, value);
        }
      }
    }
  }, []);

  const handleChange = (event, newTab) => {
    setTab(newTab);
  };

  return (
    <Box
      sx={{
        flexGrow: 1,
        bgcolor: "background.paper",
        borderRadius: 2,
        display: "flex",
        width: "90%",
        marginLeft: 2,
        "& .Mui-disabled": {
          "-webkit-text-fill-color": "inherit",
        },
      }}
    >
      <CustomAlert
        open={openAlert}
        setOpen={setOpenAlert}
        severity={AlertContent.severity}
        message={AlertContent.message}
      />
      <Tabs
        orientation="vertical"
        value={tab}
        onChange={handleChange}
        {...verticalTabStyle}
      >
        <Tab label="Cylindrical Shell" />
        <Tab label="Elliptical Head" />
        <Tab label="Nozzle Calculation" />
        <Tab label="Saddle Zick Analysis" />
        <Tab label="Stress Moment" />
        <Tab label="Wind Load" />
        <Tab label="Seismic Load" />
        <Tab label="Deflection" />
        <Tab label="Skirt Support" />
        <Tab label="Anchor Bolt" />
        <Tab label="Base Ring" />
        <Tab label="Lug Support" />
        <Tab label="Leg Support" />
      </Tabs>
      <TabPanel value={tab} index={0}>
        <Stack
          direction={"row"}
          sx={{ justifyContent: "space-between", alignItems: "center" }}
        >
          <Typography variant="h6" color="primary" marginLeft="10rem">
            Cylindrical Shell
          </Typography>
          <Button
            variant="contained"
            sx={{ ...SubmitButtonStyle }}
            onClick={getCalc}
          >
            Calculate
          </Button>
        </Stack>
        <br />
        <CylindricalShellTab
          calculationResult={calculationResult?.Cylindrical}
        />
      </TabPanel>
      <TabPanel value={tab} index={1}>
        <Stack
          direction={"row"}
          sx={{ justifyContent: "space-between", alignItems: "center" }}
        >
          <Typography variant="h6" color="primary" marginLeft="10rem">
            Elliptical Head
          </Typography>
          <Button
            variant="contained"
            sx={{ ...SubmitButtonStyle }}
            onClick={getCalc}
          >
            Calculate
          </Button>
        </Stack>
        <br />
        <EllipticalHeadTab calculationResult={calculationResult?.Elliptical} />
      </TabPanel>
      <TabPanel value={tab} index={2}>
        <Stack
          direction={"row"}
          sx={{ justifyContent: "space-between", alignItems: "center" }}
        >
          <Typography variant="h6" color="primary" marginLeft="10rem">
            Nozzle
          </Typography>
          <Button
            variant="contained"
            sx={{ ...SubmitButtonStyle }}
            onClick={getCalc}
          >
            Calculate
          </Button>
        </Stack>
        <br />
        <NozzleTab calculationResult={calculationResult?.Nozzle} />
      </TabPanel>
      <TabPanel value={tab} index={3}>
        <Stack
          direction={"row"}
          sx={{ justifyContent: "space-between", alignItems: "center" }}
        >
          <Typography variant="h6" color="primary" marginLeft="10rem">
            Saddle Analysis
          </Typography>
          <Button
            variant="contained"
            sx={{ ...SubmitButtonStyle }}
            onClick={getCalc}
          >
            Calculate
          </Button>
        </Stack>
        <br />
        <SaddleAnalysisTab calculationResult={calculationResult?.Saddle} />
      </TabPanel>
      <TabPanel value={tab} index={4}>
        <Stack
          direction={"row"}
          sx={{ justifyContent: "space-between", alignItems: "center" }}
        >
          <Typography variant="h6" color="primary" marginLeft="10rem">
            Stress Moment
          </Typography>
          <Button
            variant="contained"
            sx={{ ...SubmitButtonStyle }}
            onClick={getCalc}
          >
            Calculate
          </Button>
        </Stack>
        <br />
        <CalcTabTemplate
          inputData={Stress_Moment_InputData}
          outputData={Stress_Moment_OutputData}
        />
      </TabPanel>
      <TabPanel value={tab} index={5}>
        <Stack
          direction={"row"}
          sx={{ justifyContent: "space-between", alignItems: "center" }}
        >
          <Typography variant="h6" color="primary" marginLeft="10rem">
            Wind Load
          </Typography>
          <Button
            variant="contained"
            sx={{ ...SubmitButtonStyle }}
            onClick={getCalc}
          >
            Calculate
          </Button>
        </Stack>
        <br />
        <CalcTabTemplate
          inputData={Wind_Load_InputData}
          outputData={Wind_Load_OutputData}
        />
      </TabPanel>
      <TabPanel value={tab} index={6}>
        <Stack
          direction={"row"}
          sx={{ justifyContent: "space-between", alignItems: "center" }}
        >
          <Typography variant="h6" color="primary" marginLeft="10rem">
            Seismic Load
          </Typography>
          <Button
            variant="contained"
            sx={{ ...SubmitButtonStyle }}
            onClick={getCalc}
          >
            Calculate
          </Button>
        </Stack>
        <br />
        <CalcTabTemplate
          inputData={Seismic_Load_InputData}
          outputData={Seismic_Load_OutputData}
        />
      </TabPanel>
      <TabPanel value={tab} index={7}>
        <Stack
          direction={"row"}
          sx={{ justifyContent: "space-between", alignItems: "center" }}
        >
          <Typography variant="h6" color="primary" marginLeft="10rem">
            Deflection
          </Typography>
          <Button
            variant="contained"
            sx={{ ...SubmitButtonStyle }}
            onClick={getCalc}
          >
            Calculate
          </Button>
        </Stack>
        <br />
        <CalcTabTemplate
          inputData={Deflection_InputData}
          outputData={Deflection_OutputData}
        />
      </TabPanel>
      <TabPanel value={tab} index={8}>
        <Stack
          direction={"row"}
          sx={{ justifyContent: "space-between", alignItems: "center" }}
        >
          <Typography variant="h6" color="primary" marginLeft="10rem">
            Skirt Support
          </Typography>
          <Button
            variant="contained"
            sx={{ ...SubmitButtonStyle }}
            onClick={getCalc}
          >
            Calculate
          </Button>
        </Stack>
        <br />
        <CalcTabTemplate
          inputData={Skirt_Support_InputData}
          outputData={Skirt_Support_OutputData}
        />
      </TabPanel>
      <TabPanel value={tab} index={9}>
        <Stack
          direction={"row"}
          sx={{ justifyContent: "space-between", alignItems: "center" }}
        >
          <Typography variant="h6" color="primary" marginLeft="10rem">
            Anchor Bolt
          </Typography>
          <Button
            variant="contained"
            sx={{ ...SubmitButtonStyle }}
            onClick={getCalc}
          >
            Calculate
          </Button>
        </Stack>
        <br />
        <CalcTabTemplate
          inputData={Anchor_Bolt_InputData}
          outputData={Anchor_Bolt_OutputData}
        />
      </TabPanel>
      <TabPanel value={tab} index={10}>
        <Stack
          direction={"row"}
          sx={{ justifyContent: "space-between", alignItems: "center" }}
        >
          <Typography variant="h6" color="primary" marginLeft="10rem">
            Base Ring
          </Typography>
          <Button
            variant="contained"
            sx={{ ...SubmitButtonStyle }}
            onClick={getCalc}
          >
            Calculate
          </Button>
        </Stack>
        <br />
        <CalcTabTemplate
          inputData={Base_Ring_InputData}
          outputData={Base_Ring_OutputData}
        />
      </TabPanel>
      <TabPanel value={tab} index={11}>
        <Stack
          direction={"row"}
          sx={{ justifyContent: "space-between", alignItems: "center" }}
        >
          <Typography variant="h6" color="primary" marginLeft="10rem">
            Lug Support
          </Typography>
          <Button
            variant="contained"
            sx={{ ...SubmitButtonStyle }}
            onClick={getCalc}
          >
            Calculate
          </Button>
        </Stack>
        <br />
        <CalcTabTemplate
          inputData={Lug_Support_InputData}
          outputData={Lug_Support_OutputData}
        />
      </TabPanel>
      <TabPanel value={tab} index={12}>
        <Stack
          direction={"row"}
          sx={{ justifyContent: "space-between", alignItems: "center" }}
        >
          <Typography variant="h6" color="primary" marginLeft="10rem">
            Leg Support
          </Typography>
          <Button
            variant="contained"
            sx={{ ...SubmitButtonStyle }}
            onClick={getCalc}
          >
            Calculate
          </Button>
        </Stack>
        <br />
        <CalcTabTemplate
          inputData={Leg_Support_InputData}
          outputData={Leg_Support_OutputData}
        />
      </TabPanel>
    </Box>
  );
};

export default CalculationTab;
